<template>
  <div class="member-overview-row">
    <section class="chart-card member-statistics-card">
      <h3 class="card-title">
        {{ t('workbench.dataStats.userStats') }}
      </h3>
      <div
        ref="userPieChartRef"
        class="user-pie-chart"
      />
    </section>

    <section class="chart-card member-conversion-card">
      <div class="conversion-header">
        <h3 class="card-title">
          {{ t('workbench.dataStats.userConversion') }}
        </h3>
        <div class="overall-conversion-rate">
          {{ t('workbench.dataStats.userConversionOverall') }}
          <strong>{{ formatRate(summary.overallRate) }}</strong>
        </div>
      </div>
      <div class="conversion-list">
        <div
          v-for="(item, index) in summary.items"
          :key="item.name"
          class="conversion-item"
        >
          <div
            class="conversion-ring"
            :style="buildConversionRingStyle(item.rate, index)"
          >
            <div class="conversion-ring-inner">
              <strong>{{ formatRate(item.rate) }}</strong>
              <span>{{ t('workbench.dataStats.conversionRate') }}</span>
            </div>
          </div>
          <div class="conversion-detail">
            <div class="conversion-detail-title">
              <strong>{{ item.name }}{{ t('workbench.dataStats.totalSuffix') }}</strong>
              <span>{{ item.total }} {{ t('workbench.dataStats.unitGe') }}</span>
            </div>
            <div class="conversion-detail-row">
              <span>
                <i
                  class="conversion-dot"
                  :class="`is-${index}`"
                />{{ t('workbench.dataStats.converted') }}
              </span>
              <strong>{{ displayValue(item.converted) }}{{ t('workbench.dataStats.unitPerson') }}</strong>
            </div>
            <div class="conversion-detail-row">
              <span><i class="conversion-dot is-empty" />{{ t('workbench.dataStats.unconverted') }}</span>
              <strong>{{ displayValue(item.unconverted) }}{{ t('workbench.dataStats.unitPerson') }}</strong>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>


<script setup>
import { computed, markRaw, nextTick, onBeforeUnmount, onMounted, shallowRef, useTemplateRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import * as echarts from 'echarts'
import { getUserStatistics } from '@/api/normalChannelStatistics'

const { t, locale } = useI18n()
const COLOR_PERSONAL = '#7667E8'
const COLOR_ENTERPRISE = '#2F80ED'
const SERIES_PERSONAL = computed(() => t('workbench.dataStats.personalUser'))
const SERIES_ENTERPRISE = computed(() => t('workbench.dataStats.enterpriseUser'))

const summary = shallowRef({
  total: 0,
  overallRate: null,
  items: []
})

const userPieChartRef = useTemplateRef('userPieChartRef')
let userPieChart = null
let resizeObserver = null

function toNullableNumber(...values) {
  const value = values.find(item => item !== null && item !== undefined && item !== '')
  if (value === undefined) return null
  const number = Number(String(value).replace('%', ''))
  return Number.isFinite(number) ? number : null
}

function normalizeRate(value) {
  const number = toNullableNumber(value)
  if (number === null) return null
  const normalized = number > 0 && number <= 1 ? number * 100 : number
  return Math.min(Math.max(normalized, 0), 100)
}

function formatRate(value) {
  const rate = normalizeRate(value)
  return rate === null ? '--' : `${rate.toFixed(2)}%`
}

function displayValue(value) {
  return value === null || value === undefined ? '--' : value
}

function buildConversionRingStyle(value, index) {
  const rate = normalizeRate(value) || 0
  const color = index === 0 ? COLOR_PERSONAL : COLOR_ENTERPRISE
  return {
    background: `conic-gradient(${color} 0 ${rate}%, #f0f2f7 ${rate}% 100%)`
  }
}

function normalizeSummary(payload) {
  const memberItems = Array.isArray(payload.items) ? payload.items : []
  const total = Number(payload.total) || 0
  const defs = [
    { key: 'personal', name: SERIES_PERSONAL.value, aliases: ['个人用户', 'personal', 'personalUser'] },
    { key: 'enterprise', name: SERIES_ENTERPRISE.value, aliases: ['企业用户', 'enterprise', 'enterpriseUser'] }
  ]
  const items = defs.map((def) => {
    const source = memberItems.find(item =>
      item.name === def.name || def.aliases.includes(item.name) || item.code === def.key
    ) || {}
    const itemTotal = toNullableNumber(source.total, source.value) || 0
    const converted = toNullableNumber(
      source.convertedTotal,
      source.converted,
      source.serviceTotal,
      source.servedTotal
    )
    const explicitUnconverted = toNullableNumber(source.unconvertedTotal, source.unconverted)
    const unconverted = explicitUnconverted !== null
      ? explicitUnconverted
      : converted === null ? null : Math.max(itemTotal - converted, 0)
    const explicitRate = normalizeRate(source.conversionRate ?? source.serviceConversionRate)
    const rate = explicitRate !== null
      ? explicitRate
      : converted === null || itemTotal <= 0 ? null : converted / itemTotal * 100
    return { name: def.name, key: def.key, total: itemTotal, converted, unconverted, rate }
  })
  const convertedTotal = items.every(item => item.converted !== null)
    ? items.reduce((sum, item) => sum + item.converted, 0)
    : null
  const explicitOverallRate = normalizeRate(
    payload.overallConversionRate ?? payload.serviceConversionRate ?? payload.conversionRate
  )
  const overallRate = explicitOverallRate !== null
    ? explicitOverallRate
    : convertedTotal === null || total <= 0 ? null : convertedTotal / total * 100
  summary.value = { total, overallRate, items }
  renderUserPieChart()
}

function resolvePieLayout() {
  const mobile = (userPieChartRef.value?.clientWidth || window.innerWidth) <= 480
  return mobile
    ? {
        center: ['50%', '38%'],
        radius: ['32%', '52%'],
        legend: { orient: 'vertical', left: 'center', bottom: 6, itemGap: 10 },
        graphicLeft: '50%',
        graphicTop: '38%'
      }
    : {
        center: ['30%', '51%'],
        radius: ['38%', '60%'],
        legend: { orient: 'vertical', left: '60%', top: 'center', itemGap: 18 },
        graphicLeft: '30%',
        graphicTop: '51%'
      }
}

function renderUserPieChart() {
  if (!userPieChart) return
  const layout = resolvePieLayout()
  const total = summary.value.total
  const items = summary.value.items
  userPieChart.setOption({
    color: [COLOR_PERSONAL, COLOR_ENTERPRISE],
    tooltip: {
      trigger: 'item',
      confine: true,
      formatter: '{b}<br/>{c}（{d}%）'
    },
    legend: {
      ...layout.legend,
      icon: 'circle',
      itemWidth: 10,
      itemHeight: 10,
      data: items.map(item => item.name),
      formatter: (name) => {
        const item = items.find(current => current.name === name)
        const rate = total > 0 ? item.total / total * 100 : 0
        const unit = item?.key === 'personal'
          ? t('workbench.dataStats.unitPerson')
          : t('workbench.dataStats.unitEnterprise')
        return `{name|${name}${t('workbench.dataStats.statsSuffix')}}{value|${item.total}}{unit|${unit}}{percent|${rate.toFixed(2)}%}`
      },
      textStyle: {
        rich: {
          name: { width: 82, color: '#333', fontSize: 13 },
          value: { width: 40, color: '#333', fontSize: 13, fontWeight: 'bold', align: 'right' },
          unit: { width: 18, color: '#333', fontSize: 13, align: 'right' },
          percent: { width: 66, padding: [0, 0, 0, 10], color: '#999', fontSize: 13, align: 'right' }
        }
      }
    },
    graphic: [{
      id: 'userPieCenter',
      type: 'group',
      left: layout.graphicLeft,
      top: layout.graphicTop,
      bounding: 'raw',
      children: [
        {
          type: 'text',
          y: -11,
          style: {
            text: String(total),
            fill: '#333',
            fontSize: 30,
            fontWeight: 'bold',
            textAlign: 'center',
            textVerticalAlign: 'middle'
          }
        },
        {
          type: 'text',
          y: 17,
          style: {
            text: t('workbench.dataStats.userTotal'),
            fill: '#999',
            fontSize: 12,
            textAlign: 'center',
            textVerticalAlign: 'middle'
          }
        }
      ]
    }],
    series: [{
      name: t('workbench.dataStats.userStats'),
      type: 'pie',
      center: layout.center,
      radius: layout.radius,
      minAngle: 0,
      stillShowZeroSum: false,
      label: { show: false },
      labelLine: { show: false },
      data: items.map(item => ({ name: item.name, value: item.total }))
    }]
  }, { replaceMerge: ['legend', 'graphic', 'series'] })
}

async function loadUserStatistics() {
  try {
    const response = await getUserStatistics()
    normalizeSummary(response && response.code === 200 ? (response.data || {}) : {})
  } catch (error) {
    console.error('查询用户统计失败:', error)
    normalizeSummary({})
  }
}

watch(locale, () => {
  loadUserStatistics()
})

onMounted(async () => {
  await nextTick()
  if (userPieChartRef.value) {
    userPieChart = markRaw(echarts.init(userPieChartRef.value))
    resizeObserver = new ResizeObserver(() => {
      userPieChart?.resize()
      renderUserPieChart()
    })
    resizeObserver.observe(userPieChartRef.value)
  }
  loadUserStatistics()
})

onBeforeUnmount(() => {
  resizeObserver?.disconnect()
  userPieChart?.dispose()
})
</script>


<style lang="scss" scoped>
.member-overview-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.chart-card {
  box-sizing: border-box;
  min-width: 0;
  padding: 20px 24px 16px;
  border: 1px solid #ebeef5;
  border-radius: 2px;
  background: #fff;
}

.card-title {
  margin: 0 0 8px;
  color: #333;
  font-size: 18px;
  font-weight: bold;
  line-height: 1.4;
}

.user-pie-chart {
  width: 100%;
  height: 360px;
}

.conversion-ring {
  position: relative;
  margin: 0 auto;
  border-radius: 50%;
}

.conversion-ring-inner {
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #fff;
}

.conversion-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;

  .card-title {
    margin-bottom: 0;
  }
}

.overall-conversion-rate {
  color: #333;
  font-size: 14px;
  white-space: nowrap;

  strong {
    font-size: 16px;
  }
}

.conversion-list {
  display: flex;
  min-height: 344px;
  flex-direction: column;
  justify-content: space-around;
}

.conversion-item {
  display: grid;
  grid-template-columns: 168px minmax(0, 1fr);
  align-items: center;
  gap: 20px;
}

.conversion-ring {
  width: 144px;
  height: 144px;
}

.conversion-ring-inner {
  inset: 27px;

  strong {
    color: #222;
    font-size: 18px;
    line-height: 1.2;
  }

  span {
    margin-top: 4px;
    color: #999;
    font-size: 12px;
  }
}

.conversion-detail-title,
.conversion-detail-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.conversion-detail-title {
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid #ebeef5;
  color: #333;
  font-size: 14px;

  strong {
    font-weight: 600;
  }
}

.conversion-detail-row {
  padding: 5px 0;
  color: #333;
  font-size: 13px;

  span {
    display: inline-flex;
    align-items: center;
  }

  strong {
    font-weight: 400;
  }
}

.conversion-dot {
  width: 10px;
  height: 10px;
  margin-right: 8px;
  border-radius: 50%;
  background: #7667e8;

  &.is-1 {
    background: #2f80ed;
  }

  &.is-empty {
    background: #f0f2f7;
  }
}

@media (max-width: 1024px) {
  .chart-card {
    padding: 20px;
  }

  .conversion-item {
    grid-template-columns: 145px minmax(0, 1fr);
    gap: 12px;
  }
}

@media (max-width: 768px) {
  .member-overview-row {
    grid-template-columns: 1fr;
    gap: 14px;
  }

  .chart-card {
    padding: 16px;
  }
}

@media (max-width: 480px) {
  .chart-card {
    padding: 12px;
  }

  .card-title {
    font-size: 15px;
  }

  .user-pie-chart {
    height: 400px;
  }

  .conversion-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .overall-conversion-rate {
    white-space: normal;
  }

  .conversion-item {
    grid-template-columns: 112px minmax(0, 1fr);
  }

  .conversion-ring {
    width: 104px;
    height: 104px;
  }

  .conversion-ring-inner {
    inset: 19px;

    strong {
      font-size: 15px;
    }
  }
}
</style>
